<template>
    <v-dialog v-model="dialogOpen" max-width="600">
      <v-card :loading="getBinsLoading" :disabled="getBinsLoading">
        <v-card-title>Manage Bins</v-card-title>
        <v-card-text>
            <div class="d-flex flex-column ga-4">
                <div class="well mt-2 fw-medium">
                    <div>Name: {{ childUser?.name }}</div>
                    <div>Email: {{ childUser?.email }}</div>
                </div>
                <div class="well" v-if="userBins?.length">
                    <div class="d-flex ga-2">
                        <div class="flex-grow-1">
                            <!-- <v-select
                                label="Select Bin"
                                v-model="newBin"
                                :items="userBins"
                                item-title="name_with_code"
                                item-value="id"
                                variant="outlined"
                                density="comfortable"
                                hide-details="auto"
                            ></v-select> -->
                            <v-select-v1
                                label="Bin"
                                placeholder="Choose"
                                v-model="newBin"
                                :items="userBins"
                                item-title="name_with_code"
                                item-value="id"
                                density="comfortable"
                            >
                                <template #prepend-inner>
                                    <mdi-icon>lab_profile</mdi-icon>
                                </template>
                                <template v-slot:item="{ props, item }">
                                    <v-list-item v-bind="props" :title="null">
                                        <div class="fs-n1">{{ item.raw.name_with_code }}</div>
                                        <div class="d-flex ga-3 fs-n4 text-secondary">
                                            <div>Virtual Card: <span class="fw-medium">{{ item.raw.virtual_card_enabled ? 'Yes' : 'No' }}</span></div>
                                            <div>Physical Card: <span class="fw-medium">{{ item.raw.physical_card_enabled ? 'Yes' : 'No' }}</span></div>
                                        </div>
                                    </v-list-item>
                                </template>
                                <template v-slot:selection="{ item, index }">
                                    <span>
                                        {{ item.raw.name_with_code }}
                                    </span>
                                </template>
                            </v-select-v1>
                        </div>
                        <div class="flex-none">
                            <v-btn-input-comfortable text="Add" @click.prevent="addBin" :loading="addBinLoading" :disabled="addBinLoading">
                                <template #prepend>
                                    <mdi-icon>add</mdi-icon>
                                </template>
                            </v-btn-input-comfortable>
                        </div>
                    </div>
                    <div v-if="newBinError" class="fs-1 pt-2 text-error">
                        {{ newBinError }}
                    </div>
                </div>
                <div class="border rounded-lg d-flex flex-column" v-if="childUser?.bins?.length">
                    <template  v-for="(item, index) in childUser.bins" :key="index">
                        <v-divider v-if="index > 0"></v-divider>
                        <div class="d-flex py-2 ps-4 pe-2 ga-2 align-center">
                            <div class="flex-grow-1">
                                <div>{{ item.name_with_code }}</div>
                                <div class="d-flex ga-3 fs-n4 text-secondary">
                                    <div>Virtual Card: <span class="fw-medium">{{ item.virtual_card_enabled ? 'Yes' : 'No' }}</span></div>
                                    <div>Physical Card: <span class="fw-medium">{{ item.physical_card_enabled ? 'Yes' : 'No' }}</span></div>
                                </div>
                            </div>
                            <div class="flex-none">
                                <v-btn-icon-text color="danger" >
                                    <mdi-icon>delete</mdi-icon>
                                </v-btn-icon-text>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </v-card-text>
        <v-card-actions-form-modal>
          <v-btn color="btn-secondary" @click="close">Close</v-btn>
        </v-card-actions-form-modal>
      </v-card>
    </v-dialog>
  </template>
  
  <script lang="ts" setup>
    import { ref, watch, defineProps, defineEmits } from 'vue';
    import axios from '@/Plugins/axios';
    import { User } from '@/Types/Models/user';
    import { XhrError } from '@/Types/xhr-error';
    import { Bin } from '@/Types/Models/bin';
    import { useForm } from '@inertiajs/vue3';
    
    interface Props {
        modelValue: boolean; // v-model will be linked to this prop
        child?: number;           // Additional data from the parent
    }
    
    const props = defineProps<Props>();
    const emit = defineEmits(['update:modelValue']);
    
    // Internal copy of the v-model value
    const dialogOpen = ref(props.modelValue);
    const userBins = ref<Bin[]>([]);
    const childUser = ref<User>();
    const getBinsLoading = ref(false);
    const newBin = ref<number | null>(null);
    const newBinError = ref('');
    
    // Watch for changes to the v-model prop and update internal value
    watch(() => props.modelValue, (newValue) => {
        dialogOpen.value = newValue;
        if(dialogOpen.value){
            getBins();
        }
    });
    
    // Emit changes back to the parent when the internal value changes
    watch(dialogOpen, (newValue) => {
        if(!dialogOpen.value){
            clearDialogData();
        }
        emit('update:modelValue', newValue);
    });
    
    // Close modal
    function close() {
        dialogOpen.value = false;
    }

    const clearDialogData = () => {
        childUser.value = undefined;
        userBins.value = [];
        newBin.value = null;
        newBinError.value = '';
    }

    const getBins = () => {
        getBinsLoading.value = true;
        axios.get(route('bins.get', props.child)).then(({data}) => {
            console.log('data', data)
            let content = data.data;
            userBins.value = content.attachable ?? [];
            childUser.value = content.child;
        })
        .catch((reason: XhrError) => {
            reason.showErr();
        })
        .finally(() => {
            getBinsLoading.value = false;
        });
    }


    const addBinLoading = ref(false);
    const addBin = () => {
        if(newBin.value){
            addBinLoading.value = true;
            axios.post(route('bins.addToUser', {'binId': newBin.value, 'childId': childUser.value?.id})).then(({data}) => {
                console.log('data', data)
                let content = data.data;
                if(content.success){
                    let binData = content.binData
                    userBins.value = binData.attachable ?? [];
                    childUser.value = binData.child;
                    newBin.value = null;
                    newBinError.value = '';
                }
                else{
                    newBinError.value = 'Bin could not be added to this user, Please try again or contact Admin';
                }
                
            })
            .catch((reason: XhrError) => {
                reason.showErr();
                newBinError.value = reason.message;
            })
            .finally(() => {
                addBinLoading.value = false;
            });
        }
        else{
            newBinError.value = 'Bin is required';
        }
    }
</script>